import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { setSnackbar } from "../../snackbar/api/snackbar";
import { createMessage, saveMessage } from "../api/actions";

// Alert colors from the portal and website theme (assets/css/style.css). Both sites show
// the title and message as plain text inside a Bootstrap alert of this type.
export const MESSAGE_TYPES = [
  { value: "info", label: "Info", bg: "rgba(23, 162, 184, 0.9)" },
  { value: "success", label: "Success", bg: "rgba(46, 202, 139, 0.9)" },
  { value: "warning", label: "Warning", bg: "rgba(241, 116, 37, 0.9)" },
  { value: "danger", label: "Urgent", bg: "rgba(228, 63, 82, 0.9)" },
];

// The portal sign-in page shows every message that's on. The website shows "main" in its hero,
// skips "season", and shows the rest as banners at the top of the home page.
export const showsOn = (name) => {
  if (name === "main") return ["Portal sign-in", "Website hero"];
  if (name === "season") return ["Portal sign-in"];
  return ["Portal sign-in", "Website banner"];
};

export const isShowing = (message) => Number(message?.status) === 1;

// Where a new message goes. The name is what the portal and website key on.
const PLACEMENTS = [
  { name: "main", label: "Website hero and portal sign-in", help: "The box on the website home page. Only one message can use it." },
  { name: "banner", label: "Website banner and portal sign-in", help: "A banner across the top of the website home page." },
  { name: "season", label: "Portal sign-in only", help: "Doesn't appear on the website." },
];

const TITLE_MAX = 256; // Message.title is varchar(256)

export function MessagePreview({ type, title, message }) {
  const t = MESSAGE_TYPES.find((m) => m.value === type) || MESSAGE_TYPES[0];
  return (
    <Box sx={{ bgcolor: t.bg, color: "#fff", borderRadius: "6px", px: 2.5, py: 2, fontFamily: '"Nunito", "Segoe UI", Roboto, sans-serif' }}>
      <Typography component="h4" sx={{ fontSize: "1.25rem", fontWeight: 600, mb: 1, lineHeight: 1.2, color: "inherit", overflowWrap: "anywhere" }}>
        {title || " "}
      </Typography>
      <Typography component="p" sx={{ m: 0, color: "inherit", overflowWrap: "anywhere" }}>
        {message || " "}
      </Typography>
    </Box>
  );
}

const formFor = (m) => ({
  name: m?.name || "banner",
  status: isShowing(m),
  type: MESSAGE_TYPES.some((t) => t.value === m?.type) ? m.type : "info",
  title: m?.title || "",
  message: m?.message || "",
});

function MessageEditor({ open, message, mainMessage, canEdit, onClose, saveMessage, createMessage, setSnackbar }) {
  const isNew = open && !message;
  const [form, setForm] = useState(formFor(message));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(formFor(message));
    setError(null);
  }, [message?.uuid, open]);

  if (!open) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const changed = JSON.stringify(form) !== JSON.stringify(formFor(message));
  const hasHtml = /<\/?[a-z][^>]*>/i.test(form.message) || /<\/?[a-z][^>]*>/i.test(form.title);
  const invalid = !form.title.trim() || !form.message.trim() || form.title.length > TITLE_MAX;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        status: form.status ? 1 : 0,
        type: form.type,
        title: form.title.trim(),
        message: form.message.trim(),
      };
      if (isNew) await createMessage({ ...body, name: form.name });
      else await saveMessage(message.uuid, body);
      const verb = isNew ? "added" : "saved";
      setSnackbar(true, "success", form.status ? `Message ${verb}. It's showing on the customer site.` : `Message ${verb}. It's hidden.`);
      onClose();
    } catch (e) {
      setError("The message didn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canEdit || saving;

  return (
    <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap>{isNew ? "New message" : "Edit message"}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {form.name} · shows on {showsOn(form.name).join(" and ").toLowerCase()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2.5, display: "grid", gap: 2.5, overflowY: "auto" }}>
        {isNew && (
          <Box sx={{ display: "grid", gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Where it shows</Typography>
            <RadioGroup value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}>
              {PLACEMENTS.map((p) => {
                const taken = p.name === "main" && mainMessage;
                return (
                  <FormControlLabel
                    key={p.name}
                    value={p.name}
                    disabled={Boolean(taken) || saving}
                    control={<Radio size="small" />}
                    sx={{ alignItems: "flex-start", m: 0, "& .MuiRadio-root": { pt: 0.25 } }}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {taken ? `In use by "${mainMessage.title || "Untitled"}". Edit that message instead.` : p.help}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
        )}

        <Box>
          <FormControlLabel
            control={<Switch checked={form.status} disabled={disabled} color="success" onChange={(e) => setForm({ ...form, status: e.target.checked })} />}
            label={<Typography sx={{ fontWeight: 500 }}>Show on the customer site</Typography>}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {form.status ? "Customers see it as soon as you save." : "Hidden. Customers won't see it."}
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Color</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.type}
            disabled={disabled}
            onChange={(e, v) => { if (v) setForm({ ...form, type: v }); }}
            aria-label="Message color"
          >
            {MESSAGE_TYPES.map((t) => (
              <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none", gap: 1, px: 1.5 }}>
                <Box component="span" sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: t.bg }} />
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <TextField
          label="Title"
          value={form.title}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          error={form.title.length > TITLE_MAX || (changed && !form.title.trim())}
          helperText={form.title.length > TITLE_MAX ? `Keep it under ${TITLE_MAX} characters.` : " "}
          inputProps={{ maxLength: TITLE_MAX + 50 }}
          fullWidth
        />
        <TextField
          label="Message"
          value={form.message}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          error={changed && !form.message.trim()}
          helperText={`${form.message.length} characters. Plain text in one paragraph: the site ignores line breaks.`}
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
        />
        {hasHtml && (
          <Alert severity="warning">The customer site shows text exactly as typed, so HTML tags like &lt;p&gt; will appear on the page. Remove them.</Alert>
        )}

        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Preview</Typography>
          <Box sx={{ opacity: form.status ? 1 : 0.5 }}>
            <MessagePreview type={form.type} title={form.title} message={form.message} />
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {canEdit && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" color="secondary" onClick={save} disabled={(!isNew && !changed) || invalid || saving} sx={{ textTransform: "none" }}>
              {saving ? "Saving…" : isNew ? "Add message" : "Save"}
            </Button>
            <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default connect(null, { saveMessage, createMessage, setSnackbar })(MessageEditor);
